import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  Hammer, Plus, X, Search, Check, Trash2, Clock, ChevronRight, ChevronLeft, Upload,
  Send, MessageSquarePlus, FileText, Receipt, Truck, CheckCircle2, Circle,
  AlertCircle, IndianRupee, Edit3, Loader2, Users, Wallet, ListChecks
} from 'lucide-react';
import { MediaViewer } from '../components/shared/MediaComponents';
import ChatInput from '../components/shared/ChatInput';

/* ═══════════════════ HELPERS ═══════════════════ */
const fmt = n => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = d => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); } catch { return d; } };
const fmtDT = d => { if (!d) return ''; try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };
const isImg = u => /\.(png|jpe?g|gif|webp|bmp)$/i.test(u || '');
const prettyStatus = s => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const ST = {
  draft:                  { label: 'Draft',           c: 'bg-gray-100 text-gray-600' },
  pending_admin_approval: { label: 'Admin Approval',  c: 'bg-amber-100 text-amber-700' },
  pending_md_approval:    { label: 'MD Approval',     c: 'bg-amber-100 text-amber-700' },
  approved:               { label: 'Approved',        c: 'bg-indigo-100 text-indigo-700' },
  in_progress:            { label: 'In Progress',     c: 'bg-sky-100 text-sky-700' },
  supervisor_completed:   { label: 'Site Completed',  c: 'bg-teal-100 text-teal-700' },
  pending_accountant1:    { label: 'Accountant 1',    c: 'bg-purple-100 text-purple-700' },
  pending_accountant2:    { label: 'Accountant 2',    c: 'bg-purple-100 text-purple-700' },
  pending_bill_admin:     { label: 'Bill · Admin',    c: 'bg-orange-100 text-orange-700' },
  pending_bill_md:        { label: 'Bill · MD',       c: 'bg-orange-100 text-orange-700' },
  bill_approved:          { label: 'Bill Approved',   c: 'bg-green-100 text-green-700' },
  payment_pending:        { label: 'Payment Pending', c: 'bg-rose-100 text-rose-700' },
  pending_final_admin:    { label: 'Final · Admin',   c: 'bg-orange-100 text-orange-700' },
  pending_final_md:       { label: 'Final · MD',      c: 'bg-orange-100 text-orange-700' },
  completed:              { label: 'Completed',       c: 'bg-gray-800 text-white' },
};
const stMeta = s => ST[s] || { label: s, c: 'bg-gray-100 text-gray-600' };
const STAGES = Object.keys(ST).filter(s => s !== 'draft'); // 14 forward stages shown on the bar

const APPROVE = {
  pending_admin_approval: { roles: ['admin', 'md'], label: 'Approve (Admin)' },
  pending_md_approval:    { roles: ['admin', 'md'],          label: 'Approve (MD)' },
  pending_bill_admin:     { roles: ['admin', 'md'], label: 'Approve Bill (Admin)' },
  pending_bill_md:        { roles: ['admin', 'md'],          label: 'Approve Bill (MD)' },
  pending_final_admin:    { roles: ['admin', 'md'], label: 'Approve Completion (Admin)' },
  pending_final_md:       { roles: ['admin', 'md'],          label: 'Approve Completion (MD)' },
};

const FLOW = {
  approved:             { next: 'in_progress',          roles: ['admin', 'md', 'manager', 'supervisor'],              label: 'Start Work' },
  in_progress:          { next: 'supervisor_completed',  roles: ['admin', 'md', 'manager', 'supervisor'],              label: 'Mark Site Complete', note: true },
  supervisor_completed: { next: 'pending_accountant1',   roles: ['admin', 'md', 'manager', 'supervisor', 'accounts'],  label: 'Send to Accounts' },
  pending_accountant1:  { next: 'pending_accountant2',   roles: ['admin', 'md', 'accounts'],                           label: 'Accountant-1 Verify', note: true },
  pending_accountant2:  { next: 'pending_bill_admin',    roles: ['admin', 'md', 'accounts'],                           label: 'Accountant-2 Verify → Bill', note: true },
  bill_approved:        { next: 'payment_pending',       roles: ['admin', 'md', 'accounts'],                           label: 'Record Payment', payment: true },
  payment_pending:      { next: 'pending_final_admin',   roles: ['admin', 'md', 'accounts'],                           label: 'Submit for Final Approval' },
};

const CAN_CREATE = ['admin', 'md', 'manager', 'supervisor'];
const FIN_ROLES = ['admin', 'md', 'accounts'];          // who sees money + status bar (status bar: admin/md)
const STATUS_BAR_ROLES = ['admin', 'md'];
const UPDATE_TYPE_C = { approval: 'border-green-300 bg-green-50', media: 'border-violet-300 bg-violet-50', note: 'border-gray-200 bg-gray-50', progress: 'border-sky-300 bg-sky-50' };

// Per-stage detail shown when a status-bar stage is clicked
function stageLines(wo, stage) {
  const L = [];
  const add = (label, val) => { if (val) L.push([label, val]); };
  switch (stage) {
    case 'pending_admin_approval': add('Admin approved', fmtDT(wo.admin_approved_at)); break;
    case 'pending_md_approval':    add('MD approved', fmtDT(wo.md_approved_at)); break;
    case 'in_progress':            add('Started', fmtDate(wo.start_date)); break;
    case 'supervisor_completed':   add('Site completed', fmtDT(wo.supervisor_completed_at)); add('Notes', wo.completion_notes); break;
    case 'pending_accountant1':    add('Accountant-1 verified', fmtDT(wo.accountant1_verified_at)); add('Notes', wo.accountant1_notes); break;
    case 'pending_accountant2':    add('Accountant-2 verified', fmtDT(wo.accountant2_verified_at)); add('Notes', wo.accountant2_notes); break;
    case 'pending_bill_admin':     add('Bill admin-approved', fmtDT(wo.bill_admin_approved_at)); break;
    case 'pending_bill_md':        add('Bill MD-approved', fmtDT(wo.bill_md_approved_at)); break;
    case 'payment_pending':        add('Payment', wo.payment_amount ? '₹' + fmt(wo.payment_amount) : null); add('Ref', wo.payment_ref); add('Received', fmtDT(wo.payment_received_at)); break;
    case 'pending_final_admin':    add('Final admin-approved', fmtDT(wo.final_admin_approved_at)); break;
    case 'pending_final_md':       add('Final MD-approved', fmtDT(wo.final_md_approved_at)); break;
    case 'completed':              add('Completed', fmtDT(wo.completed_at)); break;
    default: break;
  }
  return L;
}

// Timeline updates (incl. uploaded files) that occurred while the WO was in this stage.
function stageActivity(wo, updates, stage) {
  if (!updates || !updates.length) return [];
  const enter = {
    pending_admin_approval: wo.created_at,
    pending_md_approval: wo.admin_approved_at,
    approved: wo.md_approved_at,
    in_progress: wo.md_approved_at,
    supervisor_completed: wo.supervisor_completed_at,
    pending_accountant1: wo.supervisor_completed_at,
    pending_accountant2: wo.accountant1_verified_at,
    pending_bill_admin: wo.accountant2_verified_at,
    pending_bill_md: wo.bill_admin_approved_at,
    bill_approved: wo.bill_md_approved_at,
    payment_pending: wo.bill_md_approved_at,
    pending_final_admin: wo.payment_received_at,
    pending_final_md: wo.final_admin_approved_at,
    completed: wo.final_md_approved_at || wo.completed_at,
  };
  const startRaw = enter[stage];
  const start = startRaw ? new Date(startRaw).getTime() : null;
  let end = Infinity;
  if (start != null) {
    const idx = STAGES.indexOf(stage);
    for (let j = idx + 1; j < STAGES.length; j++) {
      const t = enter[STAGES[j]];
      if (t && new Date(t).getTime() > start) { end = new Date(t).getTime(); break; }
    }
  }
  return updates.filter(u => {
    if (u.stage === stage) return true;
    if (u.stage) return false;
    if (start == null) return false;
    const t = new Date(u.created_at).getTime();
    return t >= start && t < end;
  });
}

/* ═══════════════════ MAIN PAGE (LIST) ═══════════════════ */
export default function WorkOrders() {
  const { user } = useAuth();
  const role = user?.role;
  const isApprover = role === 'admin' || role === 'md';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [projects, setProjects] = useState([]);
  const [formInit, setFormInit] = useState(null);
  const [detailId, setDetailId] = useState(null);

  // Auto-open WO from ?id= query param (e.g. /wo-dashboard.html rows link here)
  useEffect(() => {
    const qid = new URLSearchParams(window.location.search).get('id');
    if (qid) {
      const n = parseInt(qid, 10);
      if (!isNaN(n)) setDetailId(n);
    }
  }, [setDetailId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = tab === 'pending' ? '/work-orders/pending-approvals' : '/work-orders';
      const r = await api.get(url);
      setList(r.data.workOrders || []);
    } catch { toast.error('Failed to load work orders'); }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/projects').then(r => setProjects(r.data.projects || r.data || [])).catch(() => {}); }, []);

  const handleListDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this work order? This cannot be undone.')) return;
    try { await api.delete(`/work-orders/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const filtered = list.filter(wo => {
    if (statusFilter && wo.status !== statusFilter) return false;
    if (companyFilter && String(wo.company_id || wo.company?.id || 1) !== companyFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (wo.wo_number || '').toLowerCase().includes(q) || (wo.title || '').toLowerCase().includes(q)
      || (wo.client_wo_number || '').toLowerCase().includes(q) || (wo.project?.name || '').toLowerCase().includes(q);
  });

  if (detailId) {
    return <WoFullDetail id={detailId} user={user} onBack={() => { setDetailId(null); load(); }} onEdit={(wo) => { setDetailId(null); setFormInit(wo); }} />;
  }

  return (
    <div className="pb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center"><Hammer className="w-5 h-5 text-orange-700" /></div>
          <div>
            <div className="flex items-center gap-3"><h1 className="text-lg font-black text-gray-800 leading-none">Project Work Orders</h1><a href="/wo-dashboard.html" className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full text-xs font-bold transition" title="All Work Orders">📊 All</a><a href="/wo-dashboard.html?co=IP" className="flex items-center gap-1 px-3 py-1.5 bg-white border border-orange-300 text-orange-700 hover:bg-orange-50 rounded-full text-xs font-bold transition" title="Interior Point WOs">📊 IP</a><a href="/wo-dashboard.html?co=AS" className="flex items-center gap-1 px-3 py-1.5 bg-white border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-full text-xs font-bold transition" title="AvanaSpace WOs">📊 AS</a></div>
            <p className="text-[11px] text-gray-400">{filtered.length} order{filtered.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        {CAN_CREATE.includes(role) && (
          <button onClick={() => setFormInit({})} className="flex items-center gap-1.5 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-xl active:scale-95">
            <Plus className="w-4 h-4" /> New
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <button onClick={() => { setTab('all'); setCompanyFilter(''); }} className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${tab === 'all' && companyFilter === '' ? 'bg-orange-600 text-white' : 'bg-white text-gray-500 border'}`}>All ({list.length})</button>
        <button onClick={() => { setTab('all'); setCompanyFilter('1'); }} className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${tab === 'all' && companyFilter === '1' ? 'bg-orange-600 text-white' : 'bg-white text-orange-700 border border-orange-300'}`}>IP ({list.filter(w => (w.company_id || (w.company && w.company.id) || 1) === 1).length})</button>
        <button onClick={() => { setTab('all'); setCompanyFilter('2'); }} className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${tab === 'all' && companyFilter === '2' ? 'bg-violet-600 text-white' : 'bg-white text-violet-700 border border-violet-300'}`}>AS ({list.filter(w => (w.company_id || (w.company && w.company.id) || 1) === 2).length})</button>
        {isApprover && <button onClick={() => setTab('pending')} className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${tab === 'pending' ? 'bg-orange-600 text-white' : 'bg-white text-gray-500 border'}`}>My Approvals</button>}
      </div>

      <div className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search WO / title / project" className="w-full pl-9 pr-3 py-2 text-xs border rounded-xl" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-2 text-xs border rounded-xl bg-white max-w-[30%]">
          <option value="">All status</option>
          {Object.keys(ST).map(s => <option key={s} value={s}>{ST[s].label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-orange-500 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400"><Hammer className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No project work orders</p></div>
      ) : (
        <div className="space-y-2">
          {filtered.map(wo => {
            const m = stMeta(wo.status);
            return (
              <div key={wo.id} role="button" tabIndex={0} onClick={() => setDetailId(wo.id)} onKeyDown={(e) => { if (e.key === 'Enter') setDetailId(wo.id); }} className="w-full text-left bg-white rounded-xl border p-3 hover:border-orange-300 active:scale-[0.99] transition cursor-pointer">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-black text-orange-700">{wo.wo_number}</span>
                      {wo.company && <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase ${wo.company.short_code === 'AS' ? 'bg-violet-100 text-violet-700' : 'bg-orange-100 text-orange-700'}`}>{wo.company.short_code}</span>}
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${m.c}`}>{m.label}</span>
                    </div>
                    <p className="text-sm font-bold text-gray-800 truncate mt-0.5">{wo.title}</p>
                    <p className="text-[11px] text-gray-500 truncate">{wo.project?.name || '—'}{wo.client_wo_number ? ` · Client: ${wo.client_wo_number}` : ''}</p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                      {wo.start_date && <span>Start {fmtDate(wo.start_date)}</span>}
                      {wo.deadline && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {fmtDate(wo.deadline)}</span>}
                      {wo.creator?.name && <span>By {wo.creator.name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {(role === 'admin' || role === 'md') && (
                      <button type="button" onClick={(e) => handleListDelete(wo.id, e)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Delete">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-300 mt-1" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formInit && <WoForm projects={projects} initial={formInit} onClose={() => setFormInit(null)} onSaved={() => { setFormInit(null); load(); }} />}
    </div>
  );
}

/* ═══════════════════ CREATE / EDIT FORM ═══════════════════ */
function WoForm({ projects, initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [f, setF] = useState({
    project_id: initial?.project_id || '', title: initial?.title || '', client_wo_number: initial?.client_wo_number || '', client_name: initial?.client_name || '', client_address: initial?.client_address || '', client_gst: initial?.client_gst || '',
    start_date: initial?.start_date ? String(initial.start_date).split('T')[0] : '',
    deadline: initial?.deadline ? String(initial.deadline).split('T')[0] : '',
    description: initial?.description || '', notes: initial?.notes || '',
    company_id: initial?.company_id || '1',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!editing && !f.project_id) { toast.error('Select a project'); return; }
    if (!f.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const body = { title: f.title.trim(), client_wo_number: f.client_wo_number || null, client_name: f.client_name || null, client_address: f.client_address || null, client_gst: f.client_gst || null, start_date: f.start_date || null, deadline: f.deadline || null, description: f.description || null, notes: f.notes || null };
      if (editing) { await api.put(`/work-orders/${initial.id}`, body); toast.success('Work order updated'); }
      else { await api.post('/work-orders', { project_id: f.project_id, company_id: parseInt(f.company_id) || 1, ...body }); toast.success('Work order created'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white">
          <h3 className="font-black text-gray-800">{editing ? 'Edit Work Order' : 'New Work Order'}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-4 space-y-3">
          {!editing && (
            <div>
              <label className="text-[11px] font-bold text-gray-500 uppercase">Company *</label>
              <select value={f.company_id} onChange={e => set('company_id', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg bg-white">
                <option value="1">Interior Point (IP)</option>
                <option value="2">Avanaspace (AS)</option>
              </select>
            </div>
          )}
          {!editing && (
            <div>
              <label className="text-[11px] font-bold text-gray-500 uppercase">Project *</label>
              <select value={f.project_id} onChange={e => set('project_id', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg bg-white">
                <option value="">Select project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div><label className="text-[11px] font-bold text-gray-500 uppercase">Title *</label>
            <input value={f.title} onChange={e => set('title', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" placeholder="e.g. False ceiling — Block A" /></div>
          <div><label className="text-[11px] font-bold text-gray-500 uppercase">Client WO No.</label>
            <input value={f.client_wo_number} onChange={e => set('client_wo_number', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" /></div>
          <div><label className="text-xs font-bold text-gray-600">Client Name</label>
            <input value={f.client_name} onChange={e => set('client_name', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" /></div>
          <div><label className="text-xs font-bold text-gray-600">Client GSTIN</label>
            <input value={f.client_gst} onChange={e => set('client_gst', e.target.value)} placeholder="33XXXXX..." maxLength={15} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg uppercase" /></div>
          <div className="md:col-span-2"><label className="text-xs font-bold text-gray-600">Client Address</label>
            <textarea value={f.client_address} onChange={e => set('client_address', e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-bold text-gray-500 uppercase">Start Date</label>
              <input type="date" value={f.start_date} onChange={e => set('start_date', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" /></div>
            <div><label className="text-[11px] font-bold text-gray-500 uppercase">Deadline</label>
              <input type="date" value={f.deadline} onChange={e => set('deadline', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" /></div>
          </div>
          <div><label className="text-[11px] font-bold text-gray-500 uppercase">Description</label>
            <textarea value={f.description} onChange={e => set('description', e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" /></div>
          <div><label className="text-[11px] font-bold text-gray-500 uppercase">Notes</label>
            <textarea value={f.notes} onChange={e => set('notes', e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 text-sm border rounded-lg" /></div>
          <button onClick={submit} disabled={saving} className="w-full py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}{editing ? 'Save Changes' : 'Create Work Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ FULL-PAGE DETAIL ═══════════════════ */
function SupervisorUploadPanel({ woId, initialCount }) {
  const [count, setCount] = useState(initialCount || 0);
  const [busy, setBusy] = useState(false);
  const upload = async (e, label) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      [...files].forEach(f => fd.append('files', f));
      fd.append('stage', label);
      await api.post(`/work-orders/${woId}/upload`, fd);
      toast.success(`${label}: ${files.length} uploaded`);
      setCount(c => c + files.length);
    } catch {
      toast.error('Upload failed');
    }
    setBusy(false);
    e.target.value = '';
  };
  return (
    <div className="bg-white rounded-lg border border-orange-300 p-2 space-y-2">
      <p className="text-[10px] font-bold uppercase text-orange-700 flex items-center justify-between">
        <span>📎 Upload before completing</span>
        <span className={count >= 2 ? 'text-green-700' : 'text-red-600'}>{count} file{count !== 1 ? 's' : ''} · need ≥ 2</span>
      </p>
      <div className="grid grid-cols-2 gap-2">
        <label className="cursor-pointer">
          <input type="file" className="hidden" multiple onChange={e => upload(e, 'measurement')} disabled={busy} />
          <div className="text-center px-2 py-3 border-2 border-dashed border-blue-300 rounded-lg text-xs font-bold text-blue-700 hover:bg-blue-50 active:scale-95">📏 Measurement Sheet</div>
        </label>
        <label className="cursor-pointer">
          <input type="file" className="hidden" multiple onChange={e => upload(e, 'site')} disabled={busy} />
          <div className="text-center px-2 py-3 border-2 border-dashed border-purple-300 rounded-lg text-xs font-bold text-purple-700 hover:bg-purple-50 active:scale-95">📷 Site Photos/Files</div>
        </label>
      </div>
    </div>
  );
}

function WoFullDetail({ id, user, onBack, onEdit }) {
  const role = user?.role;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [payAmt, setPayAmt] = useState('');
  const [payRef, setPayRef] = useState('');
  const [progress, setProgress] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [openStage, setOpenStage] = useState(null);
  const [stageNote, setStageNote] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/work-orders/${id}`); setData(r.data); }
    catch { toast.error('Failed to load'); onBack(); }
    setLoading(false);
  }, [id, onBack]);
  useEffect(() => { reload(); }, [reload]);

  const after = () => reload();

  const approve = async (action) => {
    setBusy(true);
    try { await api.post(`/work-orders/${id}/approve`, { action, notes: note || undefined }); toast.success(action === 'reject' ? 'Rejected' : 'Approved'); setNote(''); setShowReject(false); after(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); } setBusy(false);
  };
  const changeStatus = async (next, extra = {}) => {
    setBusy(true);
    try { await api.post(`/work-orders/${id}/status`, { status: next, notes: note || undefined, ...extra }); toast.success('Status updated'); setNote(''); setPayAmt(''); setPayRef(''); after(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); } setBusy(false);
  };
  const addProgress = async () => {
    if (!progress.trim()) return; setBusy(true);
    try { await api.post(`/work-orders/${id}/updates`, { update_text: progress.trim(), update_type: 'progress' }); setProgress(''); after(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); } setBusy(false);
  };
  const uploadFiles = async (files, stage) => {
    if (!files?.length) return; setBusy(true);
    try { const fd = new FormData(); [...files].forEach(file => fd.append('files', file)); if (stage) fd.append('stage', stage); await api.post(`/work-orders/${id}/upload`, fd); toast.success('Uploaded'); after(); }
    catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); } setBusy(false);
  };
  const addStageNote = async (stage) => {
    if (!stageNote.trim()) return; setBusy(true);
    try { await api.post(`/work-orders/${id}/updates`, { update_text: stageNote.trim(), update_type: 'note', stage }); setStageNote(''); after(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); } setBusy(false);
  };
  const del = async () => {
    if (!window.confirm('Delete this work order?')) return; setBusy(true);
    try { await api.delete(`/work-orders/${id}`); toast.success('Deleted'); onBack(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); setBusy(false); }
  };

  if (loading || !data) return <div className="fixed inset-0 z-50 bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div>;

  const wo = data.workOrder;
  const pos = data.purchaseOrders || [];
  const exps = data.expenses || [];
  const fwos = data.factoryWorkOrders || [];
  const updates = wo.updates || [];
  const m = stMeta(wo.status);
  const ap = APPROVE[wo.status];
  const fl = FLOW[wo.status];
  const canApprove = ap && ap.roles.includes(role);
  const canFlow = fl && fl.roles.includes(role);
  const canDelete = ['admin', 'md'].includes(role) && ['draft', 'pending_admin_approval'].includes(wo.status);
  const canEdit = CAN_CREATE.includes(role) && wo.status !== 'completed';
  const seesStatusBar = STATUS_BAR_ROLES.includes(role);
  const seesMoney = FIN_ROLES.includes(role);
  const curIdx = STAGES.indexOf(wo.status);

  const TABS = [
    { k: 'overview', label: 'Overview', icon: ListChecks },
    { k: 'updates', label: 'Updates', icon: MessageSquarePlus },
    { k: 'expenses', label: 'Expenses', icon: Receipt },
    { k: 'purchase', label: 'Purchase', icon: Truck },
    { k: 'files', label: 'Files', icon: FileText },
    { k: 'labour', label: 'Labour', icon: Users },
    { k: 'factory', label: 'Factory', icon: Hammer },
    ...(seesMoney ? [{ k: 'accounts', label: 'Accounts', icon: Wallet }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 overflow-y-auto">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"><ChevronLeft className="w-5 h-5" /> Back</button>
            <div className="flex items-center gap-1">
              {canEdit && <button onClick={() => onEdit(wo)} className="p-1.5 bg-gray-100 rounded-lg"><Edit3 className="w-4 h-4 text-gray-600" /></button>}
              {canDelete && <button onClick={del} disabled={busy} className="p-1.5 bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-lg font-black text-orange-700">{wo.wo_number}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${m.c}`}>{m.label}</span>
          </div>
          <h1 className="text-2xl font-black text-gray-800">{wo.title}</h1>
          <p className="text-sm mt-1"><span className="font-bold text-gray-700">Project:</span> <span className="text-gray-700">{wo.project?.name || '—'}</span>{wo.client_wo_number ? <span className="text-gray-400"> · Client WO: {wo.client_wo_number}</span> : null}</p>
        </div>

        <div className="p-4 space-y-4">
          {/* Status bar — admin/MD only */}
          {seesStatusBar && (
            <div className="bg-white rounded-xl border p-3">
              <p className="text-[10px] font-black uppercase tracking-wide text-gray-400 mb-2">Status Progress</p>
              <div className="flex gap-1 overflow-x-auto pb-1 md:justify-between">
                {STAGES.map((s, i) => {
                  const done = i < curIdx, cur = i === curIdx;
                  return (
                    <button key={s} onClick={() => setOpenStage(openStage === s ? null : s)}
                      className={`shrink-0 flex flex-col items-center gap-1 px-2 py-1 rounded-lg ${cur ? 'bg-orange-50' : ''}`}>
                      {done ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : cur ? <Circle className="w-4 h-4 text-orange-600 fill-orange-200" /> : <Circle className="w-4 h-4 text-gray-300" />}
                      <span className={`text-[8px] font-bold whitespace-nowrap ${cur ? 'text-orange-700' : done ? 'text-green-700' : 'text-gray-400'}`}>{ST[s].label}</span>
                    </button>
                  );
                })}
              </div>
              {openStage && (() => {
                const lines = stageLines(wo, openStage);
                const acts = stageActivity(wo, updates, openStage);
                return (
                  <div className="mt-2 border-t pt-2">
                    <p className="text-xs font-bold text-gray-700">{ST[openStage].label}</p>
                    {lines.length > 0 && <div className="mt-1 space-y-0.5">{lines.map(([l, v], i) => (
                      <p key={i} className="text-[11px] text-gray-600"><span className="text-gray-400">{l}:</span> {v}</p>))}</div>}
                    {acts.length > 0 && <div className="mt-2 space-y-1.5">{acts.map(u => (
                      <div key={u.id} className="text-[11px] bg-gray-50 border rounded-lg p-2">
                        <p className="text-gray-600">{u.update_text}</p>
                        {(u.media_urls || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{u.media_urls.map((mu, i) => (
                          <a key={i} href={mu} target="_blank" rel="noreferrer" className="w-12 h-12 rounded border overflow-hidden bg-white block">{isImg(mu) ? <img src={mu} alt="" className="w-full h-full object-cover" /> : <span className="text-[8px] flex items-center justify-center h-full text-gray-400">file</span>}</a>))}</div>}
                        <p className="text-[9px] text-gray-400 mt-0.5">{u.creator?.name || 'System'} · {fmtDT(u.created_at)}</p>
                      </div>))}</div>}
                    {lines.length === 0 && acts.length === 0 && <p className="text-[11px] text-gray-400 mt-1">No record for this stage yet.</p>}
                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t">
                      <input value={stageNote} onChange={e => setStageNote(e.target.value)} placeholder="Note for this stage…" className="flex-1 px-2 py-1.5 text-[11px] border rounded-lg" />
                      <button onClick={() => addStageNote(openStage)} disabled={busy || !stageNote.trim()} className="px-2 py-1.5 bg-sky-600 text-white rounded-lg disabled:opacity-50"><MessageSquarePlus className="w-3.5 h-3.5" /></button>
                      <label className="px-2 py-1.5 bg-violet-600 text-white rounded-lg cursor-pointer flex items-center"><Upload className="w-3.5 h-3.5" /><input type="file" multiple className="hidden" onChange={e => { uploadFiles(e.target.files, openStage); e.target.value = ''; }} /></label>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Action panel */}
          {(canApprove || canFlow) && (
            <div className="bg-orange-50/60 border-2 border-orange-200 rounded-xl p-3 space-y-2">
              <p className="text-[10px] font-black uppercase tracking-wide text-orange-700 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Action needed</p>
              {(fl?.note || canApprove) && !fl?.payment && <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Notes (optional)" className="w-full px-3 py-2 text-xs border rounded-lg" />}
              {fl?.next === 'supervisor_completed' && <SupervisorUploadPanel woId={wo.id} initialCount={(wo.media_urls || []).length} />}
              {fl?.payment && (
                <div className="grid grid-cols-2 gap-2">
                  <input value={payAmt} onChange={e => setPayAmt(e.target.value)} type="number" placeholder="Amount ₹" className="px-3 py-2 text-xs border rounded-lg" />
                  <input value={payRef} onChange={e => setPayRef(e.target.value)} placeholder="Payment ref" className="px-3 py-2 text-xs border rounded-lg" />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {canApprove && <button onClick={() => approve('approve')} disabled={busy} className="flex-1 min-w-[120px] py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1 disabled:opacity-50"><Check className="w-4 h-4" /> {ap.label}</button>}
                {canApprove && (showReject
                  ? <button onClick={() => approve('reject')} disabled={busy} className="flex-1 min-w-[120px] py-2 bg-red-600 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1 disabled:opacity-50"><X className="w-4 h-4" /> Confirm Reject</button>
                  : <button onClick={() => setShowReject(true)} className="py-2 px-3 bg-red-50 text-red-600 text-xs font-bold rounded-lg">Reject</button>)}
                {canFlow && <button onClick={() => changeStatus(fl.next, fl.payment ? { payment_amount: payAmt || null, payment_ref: payRef || null } : {})} disabled={busy} className="flex-1 min-w-[140px] py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1 disabled:opacity-50">{fl.payment ? <IndianRupee className="w-4 h-4" /> : <Send className="w-4 h-4" />} {fl.label}</button>}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto border-b">
            {TABS.map(t => {
              const Ic = t.icon;
              return (
                <button key={t.k} onClick={() => setActiveTab(t.k)} className={`shrink-0 flex items-center gap-1 px-3 py-2 text-xs font-bold border-b-2 ${activeTab === t.k ? 'border-orange-600 text-orange-700' : 'border-transparent text-gray-400'}`}>
                  <Ic className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {activeTab === 'overview' && (
            <div className="space-y-3">
              <div className="bg-white rounded-xl border p-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <Info label="Project" value={wo.project?.name} />
                <Info label="Company" value={wo.company?.name || 'Interior Point'} />
                <Info label="Client WO" value={wo.client_wo_number} />
                <Info label="Client Name" value={wo.client_name} />
                <Info label="Client GSTIN" value={wo.client_gst} />
                <Info label="Client Address" value={wo.client_address} />
                <Info label="Start" value={fmtDate(wo.start_date)} />
                <Info label="Deadline" value={fmtDate(wo.deadline)} />
                <Info label="Created by" value={wo.creator?.name} />
                <Info label="Created" value={fmtDate(wo.created_at)} />
              </div>
              {wo.description && <p className="text-xs text-gray-600 whitespace-pre-line bg-white border rounded-xl p-3">{wo.description}</p>}
              {wo.notes && <p className="text-[11px] text-gray-500 whitespace-pre-line bg-white border rounded-xl p-3">📝 {wo.notes}</p>}
              {wo.status === 'completed' && <div className="flex items-center justify-center gap-2 text-green-700 text-sm font-bold py-2"><CheckCircle2 className="w-5 h-5" /> Work Order Completed</div>}
            </div>
          )}

          {activeTab === 'updates' && <WoUpdatesFeed woId={wo.id} updates={updates} onReload={reload} />}

          {activeTab === 'expenses' && <LinkedList items={exps} total={exps.reduce((s, e) => s + parseFloat(e.amount || 0), 0)} empty="No expenses linked yet" render={ex => ({ title: ex.description || ('Expense #' + ex.id), right: ex.amount ? '₹' + fmt(ex.amount) : '', sub: prettyStatus(ex.status) })} icon={Receipt} />}
          {activeTab === 'purchase' && (pos.length === 0 ? <Placeholder icon={Truck} text="No purchase orders linked yet" /> : (
            <div className="space-y-2">
              {pos.map(po => (
                <div key={po.id} className="bg-white border rounded-xl p-3 text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-black text-orange-700 text-sm">{po.po_number || ('PO #' + po.id)}</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase">{prettyStatus(po.status)}</span>
                  </div>
                  {(po.items || []).length > 0 && (
                    <div className="space-y-1">
                      {po.items.map((it, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
                          <span className="text-gray-700 truncate">{it.material || it.description || '—'}</span>
                          <span className="text-gray-500 text-[10px] shrink-0 ml-2">{it.quantity || ''} {it.unit || ''}{it.estimated_cost ? ' · ₹' + fmt(it.estimated_cost) : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {po.notes && <p className="text-[10px] text-gray-500 italic bg-amber-50 border border-amber-100 rounded px-2 py-1">📝 {po.notes}</p>}
                  {(po.media || []).length > 0 && (
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Files / Drawings</p>
                      <div className="flex flex-wrap gap-1.5">
                        {po.media.map(m => {
                          const url = m.s3_url || m.url || m.file_url || (m.s3_key ? '/uploads/' + m.s3_key : '');
                          const isImage = /\.(png|jpe?g|gif|webp|bmp)$/i.test(m.file_name || url || '');
                          return (
                            <a key={m.id} href={url} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-lg border overflow-hidden bg-gray-50 hover:shadow transition">
                              {isImage ? <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="text-[8px] flex items-center justify-center h-full text-gray-500 px-1 text-center break-all">{(m.file_name || 'file').slice(0, 16)}</div>}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                    <span className="text-[10px] text-gray-400">Total</span>
                    <span className="font-bold text-gray-700">{po.effective_total ? '₹' + fmt(po.effective_total) : 'Price pending'}</span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between bg-gray-800 text-white rounded-lg px-3 py-2 text-xs font-black">
                <span>TOTAL</span>
                <span>₹{fmt(pos.reduce((s, p) => s + parseFloat(p.effective_total || 0), 0))}</span>
              </div>
            </div>
          ))}

          {activeTab === 'files' && (
            <div className="space-y-3">
              <label className="inline-flex items-center gap-1 px-3 py-2 bg-violet-600 text-white text-xs font-bold rounded-lg cursor-pointer"><Upload className="w-4 h-4" /> Upload files<input type="file" multiple className="hidden" onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} /></label>
              {(wo.media_urls || []).length === 0 ? <p className="text-xs text-gray-400 text-center py-6">No files yet</p> : (
                <div className="grid grid-cols-3 gap-2">
                  {wo.media_urls.map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noreferrer" className="block border rounded-lg overflow-hidden bg-white aspect-square">
                      {isImg(u) ? <img src={u} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><FileText className="w-6 h-6 text-gray-400" /></div>}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'labour' && <LabourTab woId={wo.id} />}
          {activeTab === 'factory' && <FactoryTab woId={wo.id} wo={wo} factoryOrders={fwos} onReload={reload} />}
          {activeTab === 'accounts' && <AccountsTab woId={wo.id} role={role} />}
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><p className="text-[10px] uppercase tracking-wide text-gray-400 font-bold">{label}</p><p className="text-gray-700 font-medium">{value || '-'}</p></div>
);

function LinkedList({ items, empty, render, icon: Ic, total }) {
  if (!items.length) return <Placeholder icon={Ic} text={empty} />;
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => { const r = render(it); const media = it.media || []; return (
        <div key={i} className="bg-white border rounded-lg px-3 py-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-700 truncate">{r.title}</span>
            <span className="text-gray-500 shrink-0 ml-2">{r.right} {r.sub || ''}</span>
          </div>
          {media.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{media.map(m => {
            const url = m.s3_url || m.url; const isImage = /\.(png|jpe?g|gif|webp|bmp)$/i.test(m.file_name || url || '');
            return <a key={m.id} href={url} target="_blank" rel="noreferrer" className="block w-14 h-14 rounded-lg border overflow-hidden bg-gray-50">{isImage ? <img src={url} alt="" className="w-full h-full object-cover" /> : <span className="text-[8px] flex items-center justify-center h-full text-gray-400 px-1 text-center break-all">{(m.file_name || 'file').slice(0, 14)}</span>}</a>;
          })}</div>}
        </div>
      ); })}
      {total != null && <div className="flex items-center justify-between bg-gray-800 text-white rounded-lg px-3 py-2 text-xs font-black mt-1"><span>TOTAL</span><span>₹{fmt(total)}</span></div>}
    </div>
  );
}

function LabourTab({ woId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ label: '', count: '1', entry_date: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/work-orders/${woId}/labour`); setData(r.data); }
    catch { toast.error('Failed to load labour'); }
    setLoading(false);
  }, [woId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.label.trim()) { toast.error('Enter labour name'); return; }
    setBusy(true);
    try { await api.post(`/work-orders/${woId}/labour`, form); setForm({ label: '', count: '1', entry_date: '' }); load(); }
    catch { toast.error('Failed'); } setBusy(false);
  };
  const del = async (lid) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.delete(`/work-orders/${woId}/labour/${lid}`); load(); } catch { toast.error('Failed'); }
  };

  if (loading || !data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-orange-500 animate-spin" /></div>;
  const list = data.labour || [];

  return (
    <div className="space-y-3">
      <div className="bg-white border rounded-xl p-3 space-y-2">
        <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">Add Labour</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_6rem_auto] gap-2">
          <input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Carpenter Raja" className="px-2 py-1.5 text-sm border rounded-lg" />
          <input type="number" min="1" value={form.count} onChange={e => setForm(p => ({ ...p, count: e.target.value }))} placeholder="Count" className="px-2 py-1.5 text-sm border rounded-lg" />
          <input type="date" value={form.entry_date} onChange={e => setForm(p => ({ ...p, entry_date: e.target.value }))} className="px-2 py-1.5 text-sm border rounded-lg" />
        </div>
        <button onClick={add} disabled={busy} className="w-full py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add</button>
      </div>

      <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
        <span className="text-[10px] font-black uppercase tracking-wide text-orange-600">Total Labour</span>
        <span className="text-lg font-black text-orange-700">{data.totalCount || 0}</span>
      </div>

      {list.length === 0 ? <p className="text-xs text-gray-400 py-3 text-center">No labour entries yet</p> : (
        <div className="space-y-1.5">
          {list.map(l => (
            <div key={l.id} className="flex items-center justify-between bg-white border rounded-lg px-3 py-2 text-xs">
              <div className="min-w-0">
                <span className="font-semibold text-gray-700">{l.label}</span>
                {l.entry_date && <span className="text-gray-400 ml-2">{fmtDate(l.entry_date)}</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-bold text-gray-700">× {l.count}</span>
                <button onClick={() => del(l.id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════ FACTORY TAB ═══════════════════ */
function FactoryTab({ woId, wo, factoryOrders, onReload }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', quantity: '', measurements: '', deadline: '' });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Item name required'); return; }
    setBusy(true);
    try {
      const payload = {
        project_id: wo.project_id || wo.project?.id,
        work_order_id: woId,
        title: form.title.trim(),
        description: form.description || null,
        quantity: form.quantity || 1,
        measurements: form.measurements || null,
        deadline: form.deadline || null,
      };
      const res = await api.post('/factory/work-orders/request', payload);
      const orderId = res.data.order?.id;
      if (orderId && files.length > 0) {
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('entity_type', 'factory_work_order');
          fd.append('entity_id', orderId);
          try { await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
        }
      }
      toast.success('Factory WO ' + (res.data.wo_number || '') + ' submitted for approval');
      setForm({ title: '', description: '', quantity: '', measurements: '', deadline: '' });
      setFiles([]);
      setShowCreate(false);
      onReload();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to create'); }
    setBusy(false);
  };

  const aBadge = (s) => ({ pending_admin: 'bg-amber-100 text-amber-700', pending_md: 'bg-blue-100 text-blue-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' }[s] || 'bg-gray-100 text-gray-600');
  const aLabel = (s) => ({ pending_admin: 'Pending Admin', pending_md: 'Pending MD', approved: 'Approved', rejected: 'Rejected' }[s] || s);
  const sBadge = (s) => ({ completed: 'bg-green-100 text-green-700', in_progress: 'bg-blue-100 text-blue-700', pending: 'bg-amber-100 text-amber-700' }[s] || 'bg-gray-100 text-gray-600');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">Factory Work Orders ({factoryOrders.length})</p>
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 text-white rounded-lg text-xs font-bold">
          <Plus className="w-3.5 h-3.5" /> Send to Factory
        </button>
      </div>

      {showCreate && (
        <div className="bg-white border-2 border-orange-200 rounded-xl p-3 space-y-2">
          <p className="text-xs font-bold text-orange-700">Create Factory Work Order Request</p>
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="Item name * (e.g. Wardrobe, TV Unit, Kitchen Cabinet)" className="w-full px-3 py-2 text-sm border rounded-lg" />
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Specifications, design notes..." rows={2} className="w-full px-3 py-2 text-sm border rounded-lg" />
          <div className="flex gap-2">
            <input type="number" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
              placeholder="Qty" className="w-20 px-3 py-2 text-sm border rounded-lg" />
            <input value={form.measurements} onChange={e => setForm(p => ({ ...p, measurements: e.target.value }))}
              placeholder="Size / Measurements (L×W×H)" className="flex-1 px-3 py-2 text-sm border rounded-lg" />
            <input type="date" value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))}
              className="px-3 py-2 text-sm border rounded-lg" />
          </div>
          <div>
            <input type="file" accept="image/*,video/*,.pdf,.doc,.docx" multiple onChange={e => setFiles(Array.from(e.target.files))}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-orange-50 file:text-orange-700" />
            {files.length > 0 && <p className="text-xs text-green-600 mt-1">{files.length} file(s) selected</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowCreate(false)} className="flex-1 py-2 border rounded-lg text-xs">Cancel</button>
            <button onClick={handleCreate} disabled={busy} className="flex-1 py-2 bg-orange-600 text-white rounded-lg text-xs font-bold disabled:opacity-50">
              {busy ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>
      )}

      {factoryOrders.length === 0 && !showCreate && (
        <Placeholder icon={Hammer} text="No factory work orders linked yet. Click 'Send to Factory' to create one." />
      )}

      {factoryOrders.map(fo => (
        <div key={fo.id} className="bg-white border rounded-xl p-3 space-y-2">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {fo.wo_number && <span className="text-xs font-mono text-gray-400">{fo.wo_number}</span>}
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${aBadge(fo.approval_status)}`}>
                  {aLabel(fo.approval_status)}
                </span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full ${sBadge(fo.status)}`}>
                  {(fo.status || '').replace('_', ' ')}
                </span>
              </div>
              <p className="font-bold text-sm mt-1">{fo.title}</p>
              {fo.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{fo.description}</p>}
              <p className="text-[10px] text-gray-400 mt-0.5">
                {fo.quantity > 1 ? 'Qty: ' + fo.quantity + ' · ' : ''}
                {fo.measurements ? fo.measurements + ' · ' : ''}
                {fo.creator?.name || ''}
                {fo.deadline ? ' · Due: ' + fmtDate(fo.deadline) : ''}
              </p>
            </div>
            <span className="text-lg font-black text-orange-600 shrink-0 ml-2">{fo.progress_percentage || 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-orange-500 h-2 rounded-full transition-all" style={{ width: (fo.progress_percentage || 0) + '%' }} />
          </div>
          {fo.media && fo.media.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Reference Files</p>
              <MediaViewer attachments={fo.media} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════ WO UPDATES FEED — WhatsApp-style ═══════════════════ */
const WO_UPDATE_BADGE = {
  approval: { label: 'Approval', bg: 'bg-green-100 text-green-700' },
  media:    { label: 'Media',    bg: 'bg-violet-100 text-violet-700' },
  note:     { label: 'Note',     bg: 'bg-gray-100 text-gray-600' },
  progress: { label: 'Progress', bg: 'bg-sky-100 text-sky-700' },
};

const isWoAudio = (url) => /\.(webm|mp3|m4a|ogg|wav|aac)$/i.test(url || '');
const isWoVideo = (url) => /\.(mp4|mov|avi|mkv)$/i.test(url || '');
const isWoVoice = (url) => /voice_\d+\.webm$/i.test(url || '');

// Date + time + relative formatter for WO update feed (Jun 2026)
function fmtWoTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const diffMin = Math.floor((new Date() - d) / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  let rel = '';
  if (diffMin < 1) rel = 'just now';
  else if (diffMin < 60) rel = diffMin + 'm ago';
  else if (diffHr < 24) rel = diffHr + 'h ago';
  else if (diffDay < 7) rel = diffDay + 'd ago';
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return rel ? `${date} · ${time} · ${rel}` : `${date} · ${time}`;
}

function WoUpdatesFeed({ woId, updates, onReload }) {
  const woFeedEndRef = useRef(null);
  const [woMediaList, setWoMediaList] = useState([]);

  // Single /media call with hard timeout — prevents gateway-timeout hangs
  const loadWoMedia = useCallback(async () => {
    try {
      const r = await api.get('/media', { params: { entity_type: 'work_order_update', entity_id: woId }, timeout: 5000 });
      setWoMediaList(r.data?.attachments || r.data?.media || r.data || []);
    } catch { setWoMediaList([]); }
  }, [woId]);

  useEffect(() => { loadWoMedia(); }, [loadWoMedia, updates]);

  // Scroll to bottom on new updates
  useEffect(() => {
    if (woFeedEndRef.current) woFeedEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [updates]);

  // Build merged timeline: WO updates + standalone media attachments
  const woFeedItems = (() => {
    const items = [];

    // WO updates (text + optional media_urls JSONB)
    (updates || []).forEach(u => {
      items.push({
        id: 'upd-' + u.id,
        kind: 'update',
        update_type: u.update_type || 'progress',
        text: u.update_text,
        media_urls: u.media_urls || [],
        creator: u.creator?.name || 'System',
        created_at: u.created_at,
        stage: u.stage,
      });
    });

    // MediaAttachments not already covered by update media_urls
    const coveredUrls = new Set(items.flatMap(i => i.media_urls || []));
    woMediaList.forEach(m => {
      const url = m.s3_url || m.url || m.file_url || (m.s3_key ? '/uploads/' + m.s3_key : null) || (m.file_path ? '/uploads/' + m.file_path.split('/').pop() : null);
      if (url && !coveredUrls.has(url)) {
        items.push({
          id: 'att-' + m.id,
          kind: 'media_attachment',
          update_type: 'media',
          text: '',
          media_urls: [url],
          file_name: m.file_name || '',
          creator: m.uploaded_by_name || m.creator?.name || '',
          created_at: m.created_at,
        });
      }
    });

    items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return items;
  })();

  // Group by date
  const woDateGroups = (() => {
    const groups = {};
    woFeedItems.forEach(item => {
      const d = new Date(item.created_at);
      const now = new Date();
      const yest = new Date(); yest.setDate(yest.getDate() - 1);
      let key;
      if (isNaN(d.getTime())) key = 'Earlier';
      else if (d.toDateString() === now.toDateString()) key = 'Today';
      else if (d.toDateString() === yest.toDateString()) key = 'Yesterday';
      else key = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  })();

  const woHandleSend = async (text) => {
    await api.post(`/work-orders/${woId}/updates`, { update_text: text, update_type: 'progress' });
    onReload();
  };

  const woHandleCreateForUpload = async (stubText) => {
    const r = await api.post(`/work-orders/${woId}/updates`, { update_text: stubText, update_type: 'media' });
    // Don't reload yet — woHandleFileUploaded will reload once after upload completes
    return r.data?.update?.id || r.data?.id || woId;
  };

  const woHandleFileUploaded = () => { onReload(); loadWoMedia(); };

  return (
    <div className="flex flex-col" style={{ minHeight: '50vh' }}>
      {/* Feed */}
      <div className="flex-1 space-y-1 pb-3">
        {woFeedItems.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <MessageSquarePlus className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No updates yet</p>
            <p className="text-[10px] mt-1 text-gray-300">Send a message, photo, or voice note</p>
          </div>
        )}

        {Object.entries(woDateGroups).map(([dateLabel, groupItems]) => (
          <div key={dateLabel}>
            {/* Date header pill */}
            <div className="flex justify-center my-3">
              <span className="text-[10px] bg-white/80 backdrop-blur border text-gray-500 px-3 py-1 rounded-full font-bold shadow-sm">{dateLabel}</span>
            </div>

            {/* Update bubbles */}
            <div className="space-y-2">
              {groupItems.map(item => {
                const badge = WO_UPDATE_BADGE[item.update_type] || { label: item.update_type || 'Update', bg: 'bg-gray-100 text-gray-600' };
                const hasMedia = (item.media_urls || []).length > 0;
                const hasText = item.text && !item.text.startsWith('📎 ') && !item.text.startsWith('🎤 ');
                const borderC = UPDATE_TYPE_C[item.update_type] || 'border-gray-200 bg-white';

                return (
                  <div key={item.id} className={`border rounded-2xl px-3 py-2.5 ${borderC} shadow-sm`}>
                    {/* Type badge + stage */}
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${badge.bg}`}>{badge.label}</span>
                      {item.stage && <span className="text-[9px] text-gray-400 font-medium">· {prettyStatus(item.stage)}</span>}
                    </div>

                    {/* Text */}
                    {hasText && <p className="text-[13px] text-gray-800 leading-relaxed whitespace-pre-wrap mb-1">{item.text}</p>}

                    {/* Media grid */}
                    {hasMedia && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {item.media_urls.map((mu, idx) => {
                          if (isWoVoice(mu) || isWoAudio(mu)) {
                            return (
                              <div key={idx} className="w-full bg-white/80 rounded-xl border p-2 flex items-center gap-2">
                                <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center shrink-0">
                                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-violet-600" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1 1.93c-3.94-.49-7-3.85-7-7.93h2c0 3.31 2.69 6 6 6s6-2.69 6-6h2c0 4.08-3.06 7.44-7 7.93V20h4v2H8v-2h4v-4.07z"/></svg>
                                </div>
                                <audio src={mu} controls className="h-8 flex-1" style={{ minWidth: 0 }} />
                              </div>
                            );
                          }
                          if (isWoVideo(mu)) {
                            return (
                              <div key={idx} className="w-full rounded-xl overflow-hidden border bg-black">
                                <video src={mu} controls className="w-full max-h-48 object-contain" preload="metadata" />
                              </div>
                            );
                          }
                          if (isImg(mu)) {
                            return (
                              <a key={idx} href={mu} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-xl border overflow-hidden bg-gray-100 hover:opacity-90 transition">
                                <img src={mu} alt="" className="w-full h-full object-cover" loading="lazy" />
                              </a>
                            );
                          }
                          // Generic file
                          const fname = item.file_name || mu.split('/').pop() || 'file';
                          return (
                            <a key={idx} href={mu} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-white/80 border rounded-xl px-3 py-2 hover:bg-gray-50 transition">
                              <FileText className="w-5 h-5 text-gray-400 shrink-0" />
                              <span className="text-xs text-gray-600 truncate max-w-[160px]">{fname}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}

                    {/* Footer: creator + time */}
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[10px] text-gray-400 font-medium">{item.creator}</span>
                      <span className="text-[10px] text-gray-400" title={new Date(item.created_at).toLocaleString('en-IN')}>{fmtWoTime(item.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div ref={woFeedEndRef} />
      </div>

      {/* ChatInput — sticky at bottom */}
      <div className="sticky bottom-0 bg-gray-50 pt-2 pb-1 -mx-4 px-4 border-t">
        <ChatInput
          onSend={woHandleSend}
          entityType="work_order_update"
          entityId={String(woId)}
          onFileUploaded={woHandleFileUploaded}
          onCreateForUpload={woHandleCreateForUpload}
          placeholder="Write an update…"
          sendColor="brand"
        />
      </div>
    </div>
  );
}

function AccountsTab({ woId, role }) {
  const canEdit = FIN_ROLES.includes(role);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editVal, setEditVal] = useState(false);
  const [vf, setVf] = useState({ total_value: '', gst_pct: '18', site_address: '' });
  const [pf, setPf] = useState({ entry_type: 'payment', amount: '', payment_date: '', reference: '', notes: '' });
  const [docs, setDocs] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/work-orders/${woId}/finance`);
      setFin(r.data);
      setVf({ total_value: r.data.total_value || '', gst_pct: r.data.gst_pct || '18', site_address: r.data.site_address || '' });
    } catch { toast.error('Failed to load accounts'); }
    try {
      const d = await api.get(`/accounts/wo-docs/${woId}`);
      setDocs(d.data);
    } catch { /* no linked docs yet */ }
    setLoading(false);
  }, [woId]);
  useEffect(() => { load(); }, [load]);

  const saveVal = async () => {
    setBusy(true);
    try { await api.put(`/work-orders/${woId}/finance`, { total_value: vf.total_value || null, gst_pct: vf.gst_pct || null, site_address: vf.site_address || null }); toast.success('Saved'); setEditVal(false); load(); }
    catch { toast.error('Failed'); } setBusy(false);
  };
  const addPay = async () => {
    if (!pf.amount) { toast.error('Amount required'); return; }
    setBusy(true);
    try { await api.post(`/work-orders/${woId}/payments`, pf); toast.success('Recorded'); setPf({ entry_type: 'payment', amount: '', payment_date: '', reference: '', notes: '' }); load(); }
    catch { toast.error('Failed'); } setBusy(false);
  };
  const delPay = async (pid) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.delete(`/work-orders/${woId}/payments/${pid}`); load(); } catch { toast.error('Failed'); }
  };

  if (loading || !fin) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-orange-500 animate-spin" /></div>;
  const s = fin.summary || {};
  const payments = fin.payments || [];

  const docSections = docs ? [
    { key: 'quotations', label: 'Quotations', color: 'indigo', items: docs.quotations || [], render: d => ({ title: d.quo_number || 'Draft', sub: (d.client_name || '') + ' · ' + fmtDate(d.quo_date), right: d.grand_total ? '₹' + fmt(d.grand_total) : '-' }) },
    { key: 'billingEntries', label: 'Billing Entries', color: 'sky', items: docs.billingEntries || [], render: d => ({ title: d.site_name || '-', sub: (d.bill_no || 'No bill #') + ' · ' + fmtDate(d.bill_date), right: d.bill_value_with_gst ? '₹' + fmt(d.bill_value_with_gst) : '-' }) },
    { key: 'invoices', label: 'Tax Invoices', color: 'amber', items: docs.invoices || [], render: d => ({ title: d.invoice_no || 'No inv #', sub: (d.client_name || '') + ' · ' + fmtDate(d.invoice_date), right: d.grand_total ? '₹' + fmt(d.grand_total) : '-' }) },
    { key: 'challans', label: 'Delivery Challans', color: 'teal', items: docs.challans || [], render: d => ({ title: d.bill_no || 'DC', sub: (d.client_name || '') + ' · ' + fmtDate(d.dc_date), right: d.grand_total ? '₹' + fmt(d.grand_total) : '-' }) },
    { key: 'vouchers', label: 'Advance Vouchers', color: 'orange', items: docs.vouchers || [], render: d => ({ title: d.invoice_no || 'Voucher', sub: (d.client_name || '') + ' · ' + fmtDate(d.invoice_date), right: d.net_amount ? '₹' + fmt(d.net_amount) : '-' }) },
    { key: 'letters', label: 'Submission Letters', color: 'purple', items: docs.letters || [], render: d => ({ title: d.client_name || '-', sub: (d.bill_no || '-') + ' · ' + fmtDate(d.letter_date), right: d.amount ? '₹' + fmt(d.amount) : '-' }) },
    { key: 'measurements', label: 'Measurement Sheets', color: 'cyan', items: docs.measurements || [], render: d => ({ title: d.project_name || 'Untitled', sub: fmtDate(d.sheet_date), right: d.grand_total ? fmt(d.grand_total) + ' sqft' : '-' }) },
    { key: 'cumulativeBills', label: 'Cumulative Bills', color: 'rose', items: docs.cumulativeBills || [], render: d => ({ title: d.bill_title || d.bill_number || 'Bill', sub: fmtDate(d.bill_date), right: d.grand_total ? '₹' + fmt(d.grand_total) : '-' }) },
  ].filter(sec => sec.items.length > 0) : [];

  return (
    <div className="space-y-3">
      {/* Value card */}
      <div className="bg-white border rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">Work Order Value</p>
          {canEdit && !editVal && <button onClick={() => setEditVal(true)} className="text-[11px] font-bold text-orange-600 flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>}
        </div>
        {editVal ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] font-bold text-gray-500 uppercase">Total Value ₹</label>
                <input type="number" value={vf.total_value} onChange={e => setVf(p => ({ ...p, total_value: e.target.value }))} className="w-full mt-0.5 px-2 py-1.5 text-sm border rounded-lg" /></div>
              <div><label className="text-[10px] font-bold text-gray-500 uppercase">GST %</label>
                <input type="number" value={vf.gst_pct} onChange={e => setVf(p => ({ ...p, gst_pct: e.target.value }))} className="w-full mt-0.5 px-2 py-1.5 text-sm border rounded-lg" /></div>
            </div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Site Address</label>
              <textarea value={vf.site_address} onChange={e => setVf(p => ({ ...p, site_address: e.target.value }))} rows={2} className="w-full mt-0.5 px-2 py-1.5 text-sm border rounded-lg" /></div>
            <div className="flex gap-2">
              <button onClick={saveVal} disabled={busy} className="flex-1 py-2 bg-orange-600 text-white text-xs font-bold rounded-lg disabled:opacity-50">Save</button>
              <button onClick={() => setEditVal(false)} className="px-3 py-2 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Info label="Total Value" value={s.base ? '₹' + fmt(s.base) : '-'} />
            <Info label={`GST (${s.gst_pct || 0}%)`} value={'₹' + fmt(s.gst_amount)} />
            <Info label="Grand Total" value={'₹' + fmt(s.grand_total)} />
            {fin.site_address && <Info label="Site Address" value={fin.site_address} />}
          </div>
        )}
      </div>

      {/* Received / Pending */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
          <p className="text-[10px] font-black uppercase tracking-wide text-green-600">Received</p>
          <p className="text-lg font-black text-green-700">₹{fmt(s.received)}</p>
        </div>
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-center">
          <p className="text-[10px] font-black uppercase tracking-wide text-rose-600">Pending</p>
          <p className="text-lg font-black text-rose-700">₹{fmt(s.pending)}</p>
        </div>
      </div>

      {/* Add payment */}
      {canEdit && (
        <div className="bg-white border rounded-xl p-3 space-y-2">
          <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">Record Advance / Payment</p>
          <div className="grid grid-cols-2 gap-2">
            <select value={pf.entry_type} onChange={e => setPf(p => ({ ...p, entry_type: e.target.value }))} className="px-2 py-1.5 text-sm border rounded-lg bg-white">
              <option value="advance">Advance</option>
              <option value="payment">Payment</option>
            </select>
            <input type="number" value={pf.amount} onChange={e => setPf(p => ({ ...p, amount: e.target.value }))} placeholder="Amount ₹" className="px-2 py-1.5 text-sm border rounded-lg" />
            <input type="date" value={pf.payment_date} onChange={e => setPf(p => ({ ...p, payment_date: e.target.value }))} className="px-2 py-1.5 text-sm border rounded-lg" />
            <input value={pf.reference} onChange={e => setPf(p => ({ ...p, reference: e.target.value }))} placeholder="Ref / UTR" className="px-2 py-1.5 text-sm border rounded-lg" />
          </div>
          <button onClick={addPay} disabled={busy} className="w-full py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"><IndianRupee className="w-4 h-4" /> Record</button>
        </div>
      )}

      {/* Payment list */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-wide text-gray-400 mb-1.5">Payment History ({payments.length})</p>
        {payments.length === 0 ? <p className="text-xs text-gray-400 py-3 text-center">No payments yet</p> : (
          <div className="space-y-1.5">
            {payments.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-white border rounded-lg px-3 py-2 text-xs">
                <div className="min-w-0">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase mr-1 ${p.entry_type === 'advance' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>{p.entry_type}</span>
                  <span className="text-gray-400">{fmtDate(p.payment_date)}{p.reference ? ` · ${p.reference}` : ''}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-gray-700">₹{fmt(p.amount)}</span>
                  {canEdit && <button onClick={() => delPay(p.id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Linked Account Documents */}
      {docSections.length > 0 && (
        <div className="border-t pt-3">
          <p className="text-[10px] font-black uppercase tracking-wide text-gray-400 mb-2">Linked Documents ({docSections.reduce((s, sec) => s + sec.items.length, 0)})</p>
          <div className="space-y-2">
            {docSections.map(sec => (
              <DocSection key={sec.key} sec={sec} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DocSection({ sec }) {
  const [open, setOpen] = useState(true);
  const goAccounts = (id, companyId) => { const qs = id ? `?type=${sec.key}&id=${id}${companyId ? '&company=' + companyId : ''}` : ''; window.location.href = '/accounts' + qs; };
  return (
    <div className="bg-white border rounded-xl p-3">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between">
        <p className={`text-[10px] font-black uppercase tracking-wide text-${sec.color}-600`}>{sec.label} ({sec.items.length})</p>
        <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="space-y-1 mt-1.5">
          {sec.items.map(d => {
            const r = sec.render(d);
            return (
              <div key={d.id} onClick={() => goAccounts(d.id, d.company_id)} className="flex items-center justify-between bg-gray-50 hover:bg-orange-50 rounded-lg px-3 py-2 text-xs cursor-pointer group transition-colors">
                <div className="min-w-0">
                  <p className="font-bold text-gray-800 truncate group-hover:text-orange-700">{r.title}</p>
                  <p className="text-[10px] text-gray-400 truncate">{r.sub}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <p className="font-bold text-gray-700">{r.right}</p>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-orange-500" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Placeholder = ({ icon: Ic, text }) => (
  <div className="text-center py-10 text-gray-400"><Ic className="w-9 h-9 mx-auto mb-2 opacity-30" /><p className="text-xs max-w-xs mx-auto">{text}</p></div>
);
